import { useEffect, useRef, useState } from "react";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const capabilities = [
  { icon: "ri-map-2-line", label: "Market validation & entry strategy" },
  { icon: "ri-user-search-line", label: "B2B outreach & lead generation" },
  { icon: "ri-phone-line", label: "Sales calls & prospect qualification" },
  { icon: "ri-check-double-line", label: "Deal closing support" },
  { icon: "ri-robot-line", label: "AI-powered follow-up systems" },
];

export default function Solution() {
  const { ref, inView } = useInView();

  return (
    <section className="bg-[#080808] py-32 px-6 relative overflow-hidden">
      {/* Large decorative text */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 font-['Cormorant_Garamond'] text-[20vw] font-light text-white/[0.015] leading-none select-none pointer-events-none whitespace-nowrap">
        SOLUTION
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: image */}
          <div className={`transition-all duration-800 ${inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/d0841c3838c1fe9d972a62c3bb7286cb.jpg"
                alt="AI-powered sales system"
                title="AI sales pipeline system for Eastern Europe"
                className="w-full h-[500px] object-cover object-top"
              />
              {/* Gold corner decorations */}
              <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#C9A84C]" />
              <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#C9A84C]" />
            </div>
          </div>

          {/* Right: content */}
          <div className={`transition-all duration-800 delay-200 ${inView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-px bg-[#C9A84C]/40" />
              <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">The Solution</span>
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-white mb-8 leading-tight">
              A smarter way to<br />
              <em className="text-[#C9A84C] not-italic">enter Eastern Europe</em>
            </h2>
            <p className="text-white/55 text-base font-light leading-relaxed font-['Outfit'] mb-10">
              EmCaLu AI Agency acts as your external sales team, combining local expertise with AI-powered systems 
              to generate leads, manage conversations and close deals — without the overhead of building 
              a local team from scratch.
            </p>

            <div className="space-y-4">
              {capabilities.map((cap, i) => (
                <div
                  key={cap.label}
                  className={`flex items-center gap-4 p-4 border border-[#C9A84C]/10 hover:border-[#C9A84C]/30 transition-all duration-300 group ${inView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`}
                  style={{ transitionDelay: `${300 + i * 80}ms` }}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 border border-[#C9A84C]/20 group-hover:border-[#C9A84C]/50 transition-colors duration-300">
                    <i className={`${cap.icon} text-[#C9A84C]/60 group-hover:text-[#C9A84C] text-sm transition-colors duration-300`} />
                  </div>
                  <span className="text-white/65 text-sm font-light font-['Outfit'] tracking-wide">{cap.label}</span>
                  <div className="ml-auto w-4 h-px bg-[#C9A84C]/20 group-hover:w-8 group-hover:bg-[#C9A84C]/50 transition-all duration-300" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
